import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { firestoreService } from '../firebase/firestore';
import entrance1 from '../assets/entrance.jpg';

const slowZoom = keyframes`
  0% { transform: scale(1); }
  100% { transform: scale(1.05); }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  50% {
    opacity: 0.4;
    transform: translateX(-50%) translateY(8px);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const wordReveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const WordSpan = styled.span`
  display: inline-block;
  opacity: 0;
  animation: ${wordReveal} 0.5s ease-out forwards;
  animation-delay: ${props => props.$delay}s;
`;

const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  overflow: hidden;
`;

const HeroImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -2;
`;

const HeroBg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  animation: ${slowZoom} 20s ease-in-out infinite alternate;
  opacity: ${props => props.opacity || 0};
  transition: opacity 2.5s cubic-bezier(0.4, 0.0, 0.2, 1);
  will-change: opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(var(--brand-shadow), rgba(0, 0, 0, 0.3));
  z-index: -1;
`;

const HeroContent = styled.div`
  z-index: 1;
  max-width: 800px;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  white-space: pre-wrap;

  @media (max-width: 768px) {
    font-size: 2.2rem;
    font-family: 'Playfair Display', serif;
    line-height: 1.25;
    letter-spacing: -0.3px;
  }

  @media (max-width: 480px) {
    font-size: 1.85rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  animation: ${fadeInUp} 1s ease-out 0.3s both;

  @media (max-width: 768px) {
    font-size: 1rem;
    font-family: 'DM Sans', sans-serif;
    max-width: 320px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const CtaButton = styled.button`
  background: linear-gradient(135deg, var(--brand-primary), var(--brand-secondary));
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 15px var(--brand-shadow);
  animation: ${fadeInUp} 1s ease-out 0.6s both;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px var(--brand-shadow-strong);
  }
  
  @media (max-width: 768px) {
    padding: 0.85rem 1.8rem;
    font-size: 0.95rem;
    border-radius: 40px;
  }

  @media (max-width: 480px) {
    padding: 0.8rem 1.5rem;
    font-size: 0.9rem;
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 1.5rem;
  color: white;
  animation: ${pulse} 2s ease-in-out infinite;
  cursor: pointer;
`;

const SlideIndicators = styled.div`
  position: absolute;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 2;
  
  @media (max-width: 768px) {
    top: 90px;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    top: 80px;
    gap: 8px;
  }
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  background: ${props => props.$active ? 'white' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: ${props => props.$active ? 1 : 0.6};
  
  &:hover {
    opacity: 1;
    transform: scale(1.2);
  }
  
  @media (max-width: 480px) {
    width: 10px;
    height: 10px;
  }
`;

const renderWords = (text, baseDelay = 0) => {
  let wordIndex = 0;
  return text.split(/(\s+|\n)/).map((part, i) => {
    if (part === '\n') return <br key={i} />;
    if (part.trim() === '') return part;
    const delay = baseDelay + wordIndex * 0.07;
    wordIndex++;
    return <WordSpan key={i} $delay={delay}>{part}</WordSpan>;
  });
};

const Hero = () => {
  const { t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [carouselImages, setCarouselImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageOpacities, setImageOpacities] = useState([]);
  const [scrollY, setScrollY] = useState(0);
  
  const defaultImages = useMemo(() => [entrance1], []);
  const images = carouselImages.length > 0 ? carouselImages : defaultImages;

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY < window.innerHeight) {
        setScrollY(window.scrollY);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadCarouselImages = async () => {
      try {
        const carouselData = await firestoreService.getAll('carousel');
        if (carouselData && carouselData.length > 0) {
          const sortedImages = carouselData
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(item => item.imageUrl)
            .filter(url => url);
          
          // Preload images before setting them
          const preloadPromises = sortedImages.map(src => {
            return new Promise((resolve, reject) => {
              const img = new Image();
              img.onload = resolve;
              img.onerror = reject;
              img.src = src;
            });
          });

          // Wait for all images to load before updating state
          Promise.all(preloadPromises)
            .then(() => {
              setCarouselImages(sortedImages);
              setLoading(false);
            })
            .catch((error) => {
              console.warn('Some carousel images failed to preload:', error);
              // Still set the images even if some failed to load
              setCarouselImages(sortedImages);
              setLoading(false);
            });
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error loading carousel images:', error);
        setLoading(false);
      }
    };

    loadCarouselImages();
  }, []);

  useEffect(() => {
    // Initialize opacity states when images change
    const initialOpacities = images.map((_, index) => index === 0 ? 1 : 0);
    setImageOpacities(initialOpacities);
    setCurrentImageIndex(0);
  }, [images]);

  useEffect(() => {
    // Preload all images for smoother transitions
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [images]);

  useEffect(() => {
    // Only start carousel if there are multiple images
    if (images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => {
          const nextIndex = (prev + 1) % images.length;
          
          // Create smooth crossfade by updating opacities
          setImageOpacities(prevOpacities => 
            prevOpacities.map((_, index) => index === nextIndex ? 1 : 0)
          );
          
          return nextIndex;
        });
      }, 6000); // Change image every 6 seconds for smoother transitions

      return () => clearInterval(interval);
    }
  }, [images.length]);

  const scrollToRooms = () => {
    const element = document.getElementById('rooms');
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  const scrollToAbout = () => {
    const element = document.getElementById('about');
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  const handleDotClick = (index) => {
    setCurrentImageIndex(index);
    setImageOpacities(prevOpacities => 
      prevOpacities.map((_, i) => i === index ? 1 : 0)
    );
  };

  return (
    <HeroSection id="home">
      <HeroImage style={{ transform: `translateY(${scrollY * 0.3}px)` }}>
        {images.map((image, index) => (
          <HeroBg
            key={index}
            src={image}
            alt={`Hoang Uyen Da Lat Homestay Entrance ${index + 1}`}
            opacity={imageOpacities[index] || 0}
          />
        ))}
        <HeroOverlay />
      </HeroImage>
      <HeroContent>
        <HeroTitle>
          {renderWords(t("Welcome to\nHoang Uyen Da Lat Homestay", "Chào mừng đến với\nHoàng Uyên Đà Lạt Homestay"))}
        </HeroTitle>
        <HeroSubtitle>
          {t("Experience the charm of Da Lat's mountains in our cozy homestay", "Trải nghiệm vẻ đẹp núi rừng Đà Lạt trong ngôi nhà ấm cúng của chúng tôi")}
        </HeroSubtitle>
        <CtaButton onClick={scrollToRooms}>
          {t("Explore Our Rooms", "Khám phá phòng của chúng tôi")}
          <i className="fas fa-arrow-right"></i>
        </CtaButton>
      </HeroContent>
      <SlideIndicators>
        {images.map((_, index) => (
          <Dot
            key={index}
            $active={index === currentImageIndex}
            onClick={() => handleDotClick(index)}
          />
        ))}
      </SlideIndicators>
      <ScrollIndicator onClick={scrollToAbout}>
        <i className="fas fa-chevron-down"></i>
      </ScrollIndicator>
    </HeroSection>
  );
};

export default Hero;